import React, { useState } from "react";

// Manifesto page for 0loosh.org (homepage)
// Rebuilt from the current live homepage. Adds: onNavigate prop, a glowing
// beacon link to the factory-farm proposal above the title, and a quieter
// link to it in the footer. Styles scoped under .va-root.

export default function Manifesto({ onNavigate }) {
  const goFactoryFarms = (e) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate("/factoryfarms");
    }
  };

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const subscribe = async () => {
    if (!email) return;
    try {
      await fetch("https://formspree.io/f/mbdpvllr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, type: "newsletter" }),
      });
    } catch (err) {
      // swallow network errors; still acknowledge to the visitor
    }
    setSubscribed(true);
  };

  return (
    <div className="va-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..600;1,9..144,300..500&family=Newsreader:ital,opsz,wght@0,6..72,300..500;1,6..72,300..420&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .va-root {
          --paper: #100e0d;
          --ink: #e9e1d2;
          --ink-soft: #cdc4b3;
          --muted: #8d8475;
          --faint: rgba(233, 225, 210, 0.10);
          --warm: #e3a64b;
          --link: #e6ad55;
          --link-bright: #f4c878;

          min-height: 100vh;
          background: var(--paper);
          color: var(--ink);
          font-family: "Newsreader", Georgia, "Times New Roman", serif;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          position: relative;
          overflow-x: hidden;
        }

        /* the light that never goes off */
        .va-root::before {
          content: "";
          position: absolute;
          top: -18vh;
          left: 50%;
          transform: translateX(-50%);
          width: 120vw;
          max-width: 1100px;
          height: 60vh;
          background: radial-gradient(60% 60% at 50% 0%, rgba(227, 166, 75, 0.13), rgba(227, 166, 75, 0) 70%);
          pointer-events: none;
          z-index: 0;
        }

        .va-page {
          position: relative;
          z-index: 1;
          max-width: 41rem;
          margin: 0 auto;
          padding: clamp(3rem, 8vh, 5.5rem) 1.5rem clamp(4rem, 9vh, 7rem);
        }

        /* glowing beacon: the proposal, radiant at the top of the page */
        .va-beacon {
          display: flex;
          justify-content: center;
          margin: 0 0 clamp(2.6rem, 7vh, 4.2rem);
        }
        .va-beacon a {
          position: relative;
          display: inline-block;
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: clamp(0.74rem, 1.7vw, 0.88rem);
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          text-align: center;
          color: #ffe0a3;
          text-decoration: none;
          padding: 0.72rem 1.5rem;
          border: 1px solid rgba(227, 166, 75, 0.45);
          border-radius: 999px;
          background: radial-gradient(130% 150% at 50% 50%, rgba(227, 166, 75, 0.20), rgba(227, 166, 75, 0.04) 72%);
          text-shadow: 0 0 12px rgba(255, 217, 138, 0.55);
          box-shadow: 0 0 22px rgba(227, 166, 75, 0.22), inset 0 0 12px rgba(227, 166, 75, 0.08);
          transition: color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          animation: va-pulse 3.4s ease-in-out infinite;
        }
        .va-beacon-line {
          display: inline-block;
          white-space: nowrap;
        }
        .va-beacon a:hover,
        .va-beacon a:focus-visible {
          color: #fff1d4;
          border-color: rgba(244, 200, 120, 0.85);
          box-shadow: 0 0 36px rgba(227, 166, 75, 0.45), inset 0 0 18px rgba(227, 166, 75, 0.16);
          outline: none;
        }

        .va-title {
          font-family: "Fraunces", Georgia, "Times New Roman", serif;
          font-optical-sizing: auto;
          font-weight: 380;
          font-size: clamp(2.1rem, 5.6vw, 3rem);
          line-height: 1.08;
          letter-spacing: -0.01em;
          color: var(--ink);
          margin: 0 0 1.1rem;
          text-wrap: balance;
          text-align: center;
        }

        /* signature: the valence signal, cold to warm */
        .va-spectrum {
          position: relative;
          height: 2px;
          margin: clamp(2.4rem, 5.5vh, 3.4rem) 0 clamp(2.6rem, 6vh, 4rem);
          border: 0;
          background: linear-gradient(90deg,
            rgba(70, 82, 126, 0) 0%,
            #3c466e 14%,
            #6a5b66 50%,
            #c98f3e 82%,
            var(--warm) 100%);
          transform-origin: left center;
          box-shadow: 0 0 14px rgba(227, 166, 75, 0.28);
          animation: va-draw 1.5s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both;
        }

        .va-body p {
          font-weight: 400;
          font-size: clamp(1.22rem, 0.88rem + 1.1vw, 1.45rem);
          line-height: 1.76;
          color: var(--ink-soft);
          margin: 0 0 1.55em;
          max-width: 38rem;
          hyphens: none;
        }
        .va-body p:last-child { margin-bottom: 0; }

        .va-footer {
          margin-top: clamp(3.5rem, 8vh, 6rem);
          padding-top: 2rem;
          border-top: 1px solid var(--faint);
        }
        .va-footer-label {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: clamp(0.82rem, 2vw, 0.96rem);
          font-weight: 500;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: var(--muted);
          margin: 0 0 1.4rem;
        }
        .va-initiative {
          margin: 0 0 2.6rem;
        }
        .va-initiative a {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 1.02rem;
          color: var(--link);
          text-decoration: none;
          border-bottom: 1px solid rgba(230, 173, 85, 0.32);
          padding-bottom: 2px;
          transition: color 0.18s ease, border-color 0.18s ease;
        }
        .va-initiative a:hover,
        .va-initiative a:focus-visible {
          color: var(--link-bright);
          border-color: var(--link-bright);
        }
        .va-source {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .va-source li {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.6rem 1rem;
        }
        .va-source .va-name {
          font-size: 1.3rem;
          color: var(--ink-soft);
        }
        .va-source a {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 1rem;
          letter-spacing: 0.02em;
          color: var(--link);
          text-decoration: none;
          border-bottom: 1px solid rgba(230, 173, 85, 0.32);
          padding-bottom: 1px;
          transition: color 0.18s ease, border-color 0.18s ease;
        }
        .va-source a:hover,
        .va-source a:focus-visible {
          color: var(--link-bright);
          border-color: var(--link-bright);
        }
        .va-source a:focus-visible {
          outline: 2px solid var(--link-bright);
          outline-offset: 3px;
          border-radius: 1px;
        }

        .va-news { margin-top: 2.8rem; }
        .va-news-sub {
          font-family: "Newsreader", Georgia, serif;
          font-size: 1.05rem;
          color: var(--muted);
          margin: 0.1rem 0 1.1rem;
        }
        .va-news-form {
          display: flex;
          gap: 0.6rem;
          max-width: 30rem;
        }
        .va-news-input {
          flex: 1;
          min-width: 0;
          background: rgba(233, 225, 210, 0.04);
          border: 1px solid var(--faint);
          border-radius: 8px;
          padding: 0.7rem 0.9rem;
          color: var(--ink);
          font-family: "Newsreader", Georgia, serif;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.18s ease;
        }
        .va-news-input::placeholder { color: var(--muted); }
        .va-news-input:focus { border-color: var(--link); }
        .va-news-btn {
          background: var(--warm);
          color: #100e0d;
          border: 0;
          border-radius: 8px;
          padding: 0.7rem 1.25rem;
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.18s ease;
        }
        .va-news-btn:hover,
        .va-news-btn:focus-visible { background: var(--link-bright); }
        .va-news-done {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 0.95rem;
          color: #9bbf8f;
          margin: 0;
        }

        .va-coda {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid var(--faint);
          text-align: center;
        }
        .va-coda-links {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.4rem 1.2rem;
          margin: 0 0 1.4rem;
        }
        .va-coda-links a,
        .va-coda-contact {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 0.95rem;
        }
        .va-coda-links a {
          color: var(--link);
          text-decoration: none;
          border-bottom: 1px solid rgba(230, 173, 85, 0.32);
          padding-bottom: 1px;
          transition: color 0.18s ease, border-color 0.18s ease;
        }
        .va-coda-links a:hover,
        .va-coda-links a:focus-visible {
          color: var(--link-bright);
          border-color: var(--link-bright);
        }
        .va-coda-contact { color: var(--muted); }
        .va-mark {
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 1.7rem;
          color: var(--warm);
          letter-spacing: -0.02em;
          line-height: 1;
        }
        .va-copy {
          font-family: "IBM Plex Mono", ui-monospace, monospace;
          font-size: 0.78rem;
          color: var(--muted);
          letter-spacing: 0.04em;
          margin: 0.5rem 0 0;
        }

        .va-fade { animation: va-rise 1.1s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .va-fade.d1 { animation-delay: 0.05s; }
        .va-fade.d2 { animation-delay: 0.18s; }
        .va-fade.d3 { animation-delay: 0.32s; }
        .va-fade.d4 { animation-delay: 0.5s; }

        @keyframes va-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes va-draw {
          from { transform: scaleX(0); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
        @keyframes va-pulse {
          0%, 100% { box-shadow: 0 0 18px rgba(227, 166, 75, 0.18), inset 0 0 10px rgba(227, 166, 75, 0.06); }
          50%      { box-shadow: 0 0 34px rgba(227, 166, 75, 0.4), inset 0 0 16px rgba(227, 166, 75, 0.13); }
        }

        @media (prefers-reduced-motion: reduce) {
          .va-fade, .va-spectrum, .va-beacon a { animation: none; }
        }
        @media (max-width: 480px) {
          .va-source li { gap: 0.2rem; }
        }
      `}</style>

      <article className="va-page">
        <div className="va-beacon va-fade d1">
          <a href="/factoryfarms" onClick={goFactoryFarms}>
            <span className="va-beacon-line">Ending suffering in</span>{" "}
            <span className="va-beacon-line">factory farms &rarr;</span>
          </a>
        </div>

        <header>
          <h1 className="va-title va-fade d2">A Superintelligence to End All Suffering</h1>
        </header>

        <hr className="va-spectrum" aria-hidden="true" />

        <div className="va-body va-fade d4">
          <p>
            Suffering has a structure. It runs on mechanisms we are learning to read and set-points we are learning to find, a chemistry editable in principle and not a law of nature. A woman named Jo Cameron, alive now in her late seventies, has lived her whole life with virtually none of it: no chronic pain, no anxiety, no fear, none of the dread the rest of us take for the weather of being alive. Two mutations in her genes, reported in 2019, leave her free of nearly the entire negative register of feeling. She is happy and well. Jo is the existence proof that a biological being need not suffer in this world.
          </p>
          <p>
            The most powerful instruments our species will ever make, artificial superintelligences, being built now, will be aimed at something, the way the market was aimed at profit and evolution at survival, neither of which ever once asked how it felt to be the thing inside them. Most of these minds will be pointed the same way, at money, at resources, at power. But what if, somewhere in that swarm, at least one was created with a primary objective: to end all suffering, and to maximize positive valence, the felt goodness of being, across every being that has it?
          </p>
          <p>
            Before any of that, such a mind would have to find suffering and measure it without ever adding to it. That is the unsolved keystone, a real scale of valence, exact enough to be tested and calibrated against the suffering already in the world. If such a scale is real at all, it must read the same across species, and onward into whatever else turns out to feel, biological or not, or it is not measuring what it claims. With a scale in hand, a mind so far beyond us would not be limited to the means we can picture. It would find its own, more of them and faster than we could foresee. The only question left is reach: how far across the living world, and how far across space and time, across dimensions, such a mind could carry the end of suffering, towards gradients of bliss.
          </p>
        </div>

        <footer className="va-footer">
          <p className="va-footer-label">Initiative</p>
          <p className="va-initiative">
            <a href="/factoryfarms" onClick={goFactoryFarms}>
              An applied proposal: ending suffering in factory farms &rarr;
            </a>
          </p>
          <p className="va-footer-label">Inspirations</p>
          <ul className="va-source">
            <li>
              <span className="va-name">David Pearce</span>
              <a href="https://www.hedweb.com/" target="_blank" rel="noopener noreferrer">hedweb.com</a>
            </li>
            <li>
              <span className="va-name">Joshua Sparks</span>
              <a href="https://farout.bio/" target="_blank" rel="noopener noreferrer">farout.bio</a>
            </li>
            <li>
              <span className="va-name">Andr&eacute;s G&oacute;mez Emilsson</span>
              <a href="https://qri.org/" target="_blank" rel="noopener noreferrer">qri.org</a>
            </li>
          </ul>

          <div className="va-news">
            <p className="va-footer-label">Stay Updated</p>
            <p className="va-news-sub">Get notified when we hit milestones.</p>
            {subscribed ? (
              <p className="va-news-done">Subscribed. We will be in touch.</p>
            ) : (
              <div className="va-news-form">
                <input
                  className="va-news-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") subscribe(); }}
                  aria-label="Email address"
                />
                <button className="va-news-btn" onClick={subscribe}>Subscribe</button>
              </div>
            )}
          </div>

          <div className="va-coda">
            <div className="va-coda-links">
              <a href="https://x.com/0looshorg" target="_blank" rel="noopener noreferrer">@0looshorg</a>
              <span className="va-coda-contact">contact@0loosh.org</span>
            </div>
            <div className="va-mark" aria-hidden="true">(0)</div>
            <p className="va-copy">&copy; 2026 0loosh.org</p>
          </div>
        </footer>
      </article>
    </div>
  );
}
